import { useContext } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { UserInvestorDetailsInterface } from '@/interfaces/user';
import { formatNumberToCurrency } from '@/utils/currency';
import HorizontalTwoValuesChart from '@/components/charts/horizontalTwoValuesChart';
import { UserContext } from '@/contexts/userContext';
import { useLocalization } from '@/hooks/useLocalization';
import { ThemeContext } from '@/contexts/themeContext';

interface DashboardAccountsHeaderInterface {
  currency?: string;
  userDetails?: UserInvestorDetailsInterface;
  assets: number;
  liabilities: number;
  isLoading: boolean;
}

export default function DashboardAccountsHeader({
  currency,
  assets,
  liabilities,
  isLoading,
}: DashboardAccountsHeaderInterface) {
  const { currentRole } = useContext(UserContext);
  const { t } = useLocalization();
  const { brandColors } = useContext(ThemeContext);

  let assetsColor = '#6FDD98';
  let liabilitiesColor = '#F29559';
  if (
    brandColors.hasPrimaryAndSecondary &&
    typeof brandColors.primary === 'string' &&
    typeof brandColors.secondary === 'string'
  ) {
    assetsColor = brandColors.primary;
    liabilitiesColor = brandColors.secondary;
  }

  const getValueDetails = (title: string, color: string, value: number) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flex: '1 0 0',
          }}
        >
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '100px',
              backgroundColor: color,
            }}
          ></Box>
          <Typography
            variant='body2'
            sx={{
              fontSize: '14px',
              lineHeight: '143%',
              letterSpacing: '0.17px',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Typography
          variant='body2'
          sx={{
            textAlign: 'right',
            fontSize: '14px',
            lineHeight: '143%',
            letterSpacing: '0.17px',
          }}
        >
          {isLoading ? (
            <Skeleton width={60} />
          ) : (
            formatNumberToCurrency(value, currency)
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded
      sx={{
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse',
          gap: 1,
          '.MuiAccordionSummary-content': {
            justifyContent: 'space-between',
          },
        }}
      >
        <Typography variant='h6'>
          {currentRole === 'owner' ? t('total') : t('netWorth')}
        </Typography>
        <Typography variant='h6'>
          {isLoading ? (
            <Skeleton width={80} />
          ) : (
            formatNumberToCurrency(assets - Math.abs(liabilities), currency)
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <HorizontalTwoValuesChart
            totalValue={assets - liabilities}
            secondValue={Math.abs(liabilities)}
            firstColor={assetsColor}
            secondColor={liabilitiesColor}
          />
          {getValueDetails(t('assets'), assetsColor, assets)}
          {getValueDetails(t('liabilities'), liabilitiesColor, liabilities)}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
